import React, { useState } from "react";
import PropTypes from "prop-types";
import { Field, Form } from "react-final-form";
import { connect } from "react-redux";
import compose from "recompose/compose";
import CardActions from "@material-ui/core/CardActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import { createStyles, withStyles } from "@material-ui/core/styles";
import { translate, useLogin, useNotify } from "react-admin";
import get from "lodash/get";
import authProvider from "../../dataproviders/authProvider";

const styles = () =>
  createStyles({
    form: {
      padding: "0 1em 1em 1em",
    },
    input: {
      marginTop: "1em",
    },
    hint: {
      marginTop: "1em",
      textAlign: "center",
    },
    button: {
      width: "100%",
    },
    actions: {
      flexDirection: "column",
    },
    backButton: {
      width: "100%",
      marginTop: "0.5em",
    },
  });

// see http://redux-form.com/6.4.3/examples/material-ui/
const renderInput = ({
  meta: { touched, error } = { touched: false, error: "" }, // eslint-disable-line react/prop-types
  input: { ...inputProps }, // eslint-disable-line react/prop-types
  ...props
}) => <TextField error={!!(touched && error)} helperText={touched && error} {...inputProps} {...props} fullWidth />;

const LoginForm = ({ classes, isLoading, translate }, props) => {
  const login = useLogin();
  const notify = useNotify();

  // Two-step MFA login: first collect credentials, then the e-mailed code.
  const [challengeId, setChallengeId] = useState(null);
  const [initiating, setInitiating] = useState(false);

  // Step 1 — exchange credentials for a challengeId (a code is mailed to the
  // user). Deliberately bypasses react-admin's login() so we are not
  // redirected before the second step succeeds.
  const submitCredentials = ({ username, password }) => {
    setInitiating(true);
    return authProvider
      .initiateMfa({ username, password })
      .then(id => {
        setInitiating(false);
        setChallengeId(id);
        notify("ra.auth.mfa_code_sent", "info");
      })
      .catch(e => {
        setInitiating(false);
        const statusCode = get(e, "message");
        if (Number(statusCode) === 429) {
          notify("ra.auth.mfa_rate_limit", "warning");
        } else if (statusCode >= 400 && statusCode < 500) {
          notify("ra.auth.incorrectLogin", "warning");
        } else {
          notify("ra.auth.sign_in_error", "error");
        }
      });
  };

  // Step 2 — verify the code. A resolved login() logs the user in and
  // react-admin redirects. On error we stay on the code screen.
  const submitCode = ({ code }) => {
    login({ challengeId, code }).catch(e => {
      const statusCode = get(e, "message");
      if (statusCode >= 400 && statusCode < 500) {
        notify("ra.auth.mfa_incorrect_code", "warning");
      } else {
        notify("ra.auth.sign_in_error", "error");
      }
    });
  };

  const backToCredentials = () => {
    setChallengeId(null);
  };

  if (challengeId) {
    return (
      <Form
        onSubmit={submitCode}
        validate={values => {
          const errors = {};
          if (!values.code) errors.code = translate("ra.validation.required");
          return errors;
        }}
      >
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div className={classes.form}>
              <div className={classes.hint}>{translate("ra.auth.mfa_code_sent")}</div>
              <div className={classes.input}>
                <Field
                  autoFocus
                  id="code"
                  name="code"
                  component={renderInput}
                  label={translate("ra.auth.mfa_code")}
                  autoComplete="one-time-code"
                  inputProps={{ inputMode: "numeric" }}
                  disabled={isLoading}
                />
              </div>
            </div>
            <CardActions className={classes.actions}>
              <Button variant="contained" type="submit" color="primary" disabled={isLoading} className={classes.button}>
                {isLoading && <CircularProgress size={25} thickness={2} />}
                {translate("ra.auth.mfa_verify")}
              </Button>
              <Button
                type="button"
                color="primary"
                disabled={isLoading}
                className={classes.backButton}
                onClick={backToCredentials}
              >
                {translate("ra.auth.mfa_back")}
              </Button>
            </CardActions>
          </form>
        )}
      </Form>
    );
  }

  return (
    <Form
      onSubmit={submitCredentials}
      validate={values => {
        const errors = {};
        if (!values.username) errors.username = translate("ra.validation.required");
        if (!values.password) errors.password = translate("ra.validation.required");
        return errors;
      }}
    >
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <div className={classes.form}>
            <div className={classes.input}>
              <Field
                autoFocus
                id="username"
                name="username"
                component={renderInput}
                label={translate("ra.auth.username")}
                disabled={isLoading || initiating}
              />
            </div>
            <div className={classes.input}>
              <Field
                id="password"
                name="password"
                component={renderInput}
                label={translate("ra.auth.password")}
                type="password"
                disabled={isLoading || initiating}
              />
            </div>
          </div>
          <CardActions>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              disabled={isLoading || initiating}
              className={classes.button}
            >
              {(isLoading || initiating) && <CircularProgress size={25} thickness={2} />}
              {translate("ra.auth.sign_in")}
            </Button>
          </CardActions>
        </form>
      )}
    </Form>
  );
};

LoginForm.propTypes = {
  classes: PropTypes.object,
  redirectTo: PropTypes.string,
};

const mapStateToProps = state => ({ isLoading: state.admin.loading > 0 });

const enhance = compose(
  withStyles(styles),
  translate,
  connect(mapStateToProps)
);

export default enhance(LoginForm);
